import { useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { Driver } from 'driver.js';
import { useAuth } from '@/features/auth/hooks/useAuth';
import {
  hasSeenGuideDashboardTour,
  markGuideDashboardTourSeen,
  shouldForceGuideDashboardTour,
  startGuideDashboardTour,
} from './guideDashboardTour';

/**
 * Starts the guide dashboard nav tour once per user (first dashboard visit),
 * or every visit when VITE_FORCE_GUIDE_DASHBOARD_TOUR=true.
 */
export function useGuideDashboardTour() {
  const { user } = useAuth();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const driverRef = useRef<Driver | null>(null);

  useEffect(() => {
    const userId = user?.id;
    if (!userId) return;

    const force = shouldForceGuideDashboardTour();
    if (!force && hasSeenGuideDashboardTour(userId)) return;

    let active = true;

    const timer = window.setTimeout(() => {
      if (!active) return;

      driverRef.current?.destroy();
      driverRef.current = startGuideDashboardTour({
        isDesktop,
        onDone: () => {
          // Ignore destroy from effect cleanup / Strict Mode remount.
          if (!active) return;
          if (!shouldForceGuideDashboardTour()) {
            markGuideDashboardTourSeen(userId);
          }
          driverRef.current = null;
        },
      });
    }, 350);

    return () => {
      active = false;
      window.clearTimeout(timer);
      driverRef.current?.destroy();
      driverRef.current = null;
    };
  }, [user?.id, isDesktop]);
}
